import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

function TopGuide(): ReactElement {
    const { t } = useTranslation('top');
    const ctaSteps = t('cta.steps', { returnObjects: true }) as string[];
    const guideTitle = t('guide.title');

    return (
        <section className="py-20 lg:py-32 bg-gray-50 relative overflow-hidden">
            {/* Decorative Wave Divider */}
            <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
                <svg
                    className="relative block w-full h-12 lg:h-20"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 1200 120"
                    preserveAspectRatio="none"
                >
                    <path
                        d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                        className="fill-white"
                    ></path>
                </svg>
            </div>
            {/* Decorative Background Elements - hidden on mobile */}
            <div className="hidden md:block absolute top-20 right-10 w-64 h-64 bg-kotomilThemaColor/5 rounded-full blur-3xl"></div>
            <div className="hidden md:block absolute bottom-20 left-10 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"></div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <h2 className="text-3xl lg:text-5xl font-black text-gray-900 mb-16 lg:mb-24 text-center tracking-tight">
                    {guideTitle}
                </h2>

                {/* Timeline Container */}
                <div className="relative">
                    {/* Vertical Line - properly connects all steps */}
                    <div className="absolute left-8 lg:left-10 top-8 lg:top-10 bottom-8 lg:bottom-10 w-1 bg-gradient-to-b from-kotomilThemaColor via-kotomilThemaColor/60 to-kotomilThemaColor/30"></div>

                    {/* Timeline Steps */}
                    <div className="space-y-8 lg:space-y-12">
                        {ctaSteps.map((step, index) => (
                            <div
                                key={index}
                                className="relative flex items-center gap-4 md:gap-8 lg:gap-12 group"
                            >
                                {/* Timeline Node - Larger and more prominent */}
                                <div className="relative z-10 flex-shrink-0">
                                    <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-kotomilThemaColor to-amber-400 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-all duration-300 border-4 border-white">
                                        <span className="text-2xl lg:text-3xl font-black text-white drop-shadow-md">
                                            {index + 1}
                                        </span>
                                    </div>
                                    {/* Outer Ring Animation */}
                                    <div className="absolute inset-0 rounded-full border-4 border-kotomilThemaColor/30 scale-110 group-hover:scale-125 transition-transform duration-300"></div>
                                    {/* Pulse Effect */}
                                    <div className="absolute inset-0 rounded-full bg-kotomilThemaColor/20 animate-ping opacity-0 group-hover:opacity-100"></div>
                                </div>

                                {/* Content Card - Enhanced with gradient and icons */}
                                <div className="flex-1 bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-8 lg:p-10 shadow-xl border-2 border-gray-100 group-hover:shadow-2xl group-hover:border-kotomilThemaColor/20 transition-all duration-300 transform group-hover:-translate-y-2 relative overflow-hidden">
                                    {/* Decorative Corner */}
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-kotomilThemaColor/10 to-transparent rounded-bl-full"></div>

                                    <p className="text-xl lg:text-2xl font-bold text-gray-900 leading-relaxed relative z-10">
                                        {step.replace(/^\d+\.\s*/, '')}
                                    </p>

                                    {/* Arrow Indicator */}
                                    <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <svg
                                            className="w-6 h-6 text-kotomilThemaColor"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M9 5l7 7-7 7"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default TopGuide;
